import { useState, useMemo, useCallback } from 'react';
import { FilterMatchMode } from 'primereact/api';
import { useGetServiceBulletinByBulletinNoQuery } from '../../../../api/endpoints/serviceBulletinReport';
import { useLanguage } from '../../../../contexts/LanguageContext';import { matchModeToOperator } from '../../../../utils/filterUtils';
const INITIAL_FILTER_STATE = {
    chassisNo: null,
    chassisNoMatchMode: FilterMatchMode.CONTAINS,
    partNo: null,
    partNoMatchMode: FilterMatchMode.CONTAINS,
    partDescription: null,
    partDescriptionMatchMode: FilterMatchMode.CONTAINS,
    partType: null,
    partTypeMatchMode: FilterMatchMode.CONTAINS,
    menge: null,
    mengeMatchMode: FilterMatchMode.CONTAINS,
    meins: null,
    meinsMatchMode: FilterMatchMode.CONTAINS,
    kurztext: null,
    kurztextMatchMode: FilterMatchMode.CONTAINS,
};

const initialLazyParams = {
    first: 0,
    rows: 10,
    page: 1,
};

const useServiceBulletinDetail = (bulletinNo) => {
    const { getCurrentLanguageId } = useLanguage();
    const [lazyParams, setLazyParams] = useState(initialLazyParams);
    const [sortField, setSortField] = useState(null);
    const [sortOrder, setSortOrder] = useState(0);
    const [activeFilters, setActiveFilters] = useState(INITIAL_FILTER_STATE);

    const languageId = getCurrentLanguageId();

    // Query params
    const queryParams = useMemo(() => {
        const params = {
            BulletinNo: bulletinNo,
            PageSize: lazyParams.rows,
            PageNumber: lazyParams.page,
            LanguageId: languageId,
        };

        const filters = buildDynamicFilters(activeFilters);
        if (filters && filters.length > 0) {
            params.Filters = filters;
        }

        const sorts = buildDynamicSorts(sortField, sortOrder);
        if (sorts && sorts.length > 0) {
            params.Sorts = sorts;
        }

        return params;
    }, [bulletinNo, lazyParams.page, lazyParams.rows, languageId, activeFilters, sortField, sortOrder]);

    // API Call
    const { data, isLoading } = useGetServiceBulletinByBulletinNoQuery(queryParams, {
        skip: !bulletinNo,
    });

    const items = useMemo(() => data?.items ?? [], [data]);
    const totalCount = useMemo(() => data?.totalCount ?? 0, [data]);
    const bulletinInfo = useMemo(() => data?.items?.[0], [data]);

    const handleFilterChange = useCallback((updatedFilters) => {
        setActiveFilters({
            chassisNo: updatedFilters.chassisNo || null,
            chassisNoMatchMode: updatedFilters.chassisNoMatchMode || FilterMatchMode.CONTAINS,
            partNo: updatedFilters.partNo || null,
            partNoMatchMode: updatedFilters.partNoMatchMode || FilterMatchMode.CONTAINS,
            partDescription: updatedFilters.partDescription || null,
            partDescriptionMatchMode: updatedFilters.partDescriptionMatchMode || FilterMatchMode.CONTAINS,
            partType: updatedFilters.partType || null,
            partTypeMatchMode: updatedFilters.partTypeMatchMode || FilterMatchMode.CONTAINS,
            menge: updatedFilters.menge || null,
            mengeMatchMode: updatedFilters.mengeMatchMode || FilterMatchMode.CONTAINS,
            meins: updatedFilters.meins || null,
            meinsMatchMode: updatedFilters.meinsMatchMode || FilterMatchMode.CONTAINS,
            kurztext: updatedFilters.kurztext || null,
            kurztextMatchMode: updatedFilters.kurztextMatchMode || FilterMatchMode.CONTAINS,
        });

        setLazyParams(initialLazyParams);
    }, []);

    const resetFilters = useCallback(() => {
        setActiveFilters(INITIAL_FILTER_STATE);
        setSortField(null);
        setSortOrder(0);
        setLazyParams(initialLazyParams);
    }, []);

    const onPage = useCallback((event) => {
        setLazyParams({
            first: event.first,
            rows: event.rows,
            page: event.page + 1,
        });
    }, []);

    const onSort = useCallback((event) => {
        const nextSortField = event?.sortField || null;
        const nextSortOrder = event?.sortOrder || 0;

        setSortField(nextSortField);
        setSortOrder(nextSortOrder);
        setLazyParams(initialLazyParams);
    }, []);

    return {
        items,
        totalCount,
        bulletinInfo,
        isLoading,
        lazyParams,
        onPage,
        activeFilters,
        handleFilterChange,
        resetFilters,
        sortField,
        sortOrder,
        onSort,
    };
};

export default useServiceBulletinDetail;

// Helper functions
const backendColumnMap = {
    chassisNo: 'ChassisNo',
    partNo: 'PartNo',
    partDescription: 'PartDescription',
    partType: 'PartType',
    menge: 'Menge',
    meins: 'Meins',
    kurztext: 'Kurztext',
};

const getBackendColumnName = (field) => {
    if (!field) return null;
    return backendColumnMap[field] || null;
};

const getOperatorValue = (matchMode) => {
    return matchModeToOperator[matchMode] || matchModeToOperator[FilterMatchMode.CONTAINS];
};

const buildDynamicFilters = (filtersState) => {
    const filters = [];

    const pushFilter = (field, value, matchMode) => {
        if (value === null || value === undefined || value === '') return;
        const column = getBackendColumnName(field);
        if (!column) return;

        let filterValue = String(value).trim();
        if (filterValue === '') return;

        filters.push({
            column,
            operator: getOperatorValue(matchMode),
            value: filterValue,
        });
    };

    pushFilter('chassisNo', filtersState?.chassisNo, filtersState?.chassisNoMatchMode);
    pushFilter('partNo', filtersState?.partNo, filtersState?.partNoMatchMode);
    pushFilter('partDescription', filtersState?.partDescription, filtersState?.partDescriptionMatchMode);
    pushFilter('partType', filtersState?.partType, filtersState?.partTypeMatchMode);
    pushFilter('menge', filtersState?.menge, filtersState?.mengeMatchMode);
    pushFilter('meins', filtersState?.meins, filtersState?.meinsMatchMode);
    pushFilter('kurztext', filtersState?.kurztext, filtersState?.kurztextMatchMode);

    return filters;
};

const buildDynamicSorts = (field, order) => {
    if (!field || order === 0) return null;
    const column = getBackendColumnName(field);
    if (!column) return null;

    return [
        {
            column,
            direction: order === 1 ? 1 : 2,
        },
    ];
};